import React from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { InfoTooltip, radius } from "@libertify/ui";
import type { AddonsSummary } from "types/";
import { AnalyticsBarChart } from "../AnalyticsBarChart";
import { AnalyticsTable } from "../AnalyticsTable";
import { formatDuration } from "../utils/formatDuration";
import { formatPct } from "../utils/formatPct";
import { AnalyticsCard } from "../AnalyticsCard";
import { barChartOptions, hbarPlotHeight } from "./utils/chartSizing";
import {
  ADDON_COLORS,
  avgSecondsPerOpen,
  FUNNEL_COLORS,
  percent,
  visibleAddons,
  type VisibleAddon,
} from "./utils/metrics";

const T_PREFIX = "media.projects.analytics.addons";
const DASH = "—";

export type AddonsOverviewPanelProps = {
  summary: AddonsSummary;
  /** Shown next to each card title. */
  badge?: React.ReactNode;
};

export const AddonsOverviewPanel: React.FC<AddonsOverviewPanelProps> = ({
  summary,
  badge,
}) => {
  const t = useTranslate();
  const rows = visibleAddons(summary.addons).sort(
    (a, b) => b.sessions_opened - a.sessions_opened
  );

  const name = (addon: string) => t(`${T_PREFIX}.names.${addon}`);
  const stages = [
    { key: "opened", value: (a: VisibleAddon) => a.sessions_opened },
    { key: "interacted", value: (a: VisibleAddon) => a.sessions_interacted },
    {
      key: "completed",
      value: (a: VisibleAddon) => a.sessions_completed ?? null,
    },
  ] as const;

  const columns: ColumnsType<VisibleAddon> = [
    {
      title: t(`${T_PREFIX}.overview.colAddon`),
      key: "addon",
      render: (_, a) => (
        <div>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: radius.circle,
              marginRight: 8,
              background: ADDON_COLORS[a.addon],
            }}
          />
          {name(a.addon)}
          <Typography.Text
            type="secondary"
            style={{ display: "block", fontSize: 11 }}
          >
            {t(`${T_PREFIX}.rule.${a.addon}`)}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <ColumnTitle
          title={t(`${T_PREFIX}.stage.opened`)}
          info={t(`${T_PREFIX}.overview.openedInfo`)}
        />
      ),
      key: "opened",
      align: "center",
      render: (_, a) => (
        <>
          {a.sessions_opened.toLocaleString()}{" "}
          <Typography.Text type="secondary">
            {`· ${formatPct(percent(a.sessions_opened, summary.project_sessions))}`}
          </Typography.Text>
        </>
      ),
    },
    {
      title: (
        <ColumnTitle
          title={t(`${T_PREFIX}.stage.interacted`)}
          info={t(`${T_PREFIX}.overview.usedInfo`)}
        />
      ),
      key: "interacted",
      align: "center",
      render: (_, a) => a.sessions_interacted.toLocaleString(),
    },
    {
      title: (
        <ColumnTitle
          title={t(`${T_PREFIX}.stage.completed`)}
          info={t(`${T_PREFIX}.overview.completedInfo`)}
        />
      ),
      key: "completed",
      align: "center",
      render: (_, a) =>
        a.sessions_completed == null ? (
          DASH
        ) : (
          <>
            {a.sessions_completed.toLocaleString()}{" "}
            {/* No opens means no rate to show, so the count stands alone. */}
            {a.sessions_opened > 0 ? (
              <Typography.Text type="secondary">
                {`· ${formatPct(percent(a.sessions_completed, a.sessions_opened))}`}
              </Typography.Text>
            ) : null}
          </>
        ),
    },
    {
      title: (
        <ColumnTitle
          title={t(`${T_PREFIX}.overview.colCoverage`)}
          info={t(`${T_PREFIX}.overview.coverageInfo`)}
        />
      ),
      key: "coverage",
      align: "center",
      // Unknown coverage is a dash, never 0%.
      render: (_, a) =>
        a.avg_session_coverage == null
          ? DASH
          : formatPct(a.avg_session_coverage * 100),
    },
    {
      title: (
        <ColumnTitle
          title={t(`${T_PREFIX}.overview.colAvgTime`)}
          info={t(`${T_PREFIX}.overview.avgTimeInfo`)}
        />
      ),
      key: "avgTime",
      align: "center",
      render: (_, a) => {
        const avg = avgSecondsPerOpen(a);
        // Same format as the add-on panel's average time card.
        return avg == null ? DASH : formatDuration(avg);
      },
    },
  ];

  return (
    <Flex vertical gap={16}>
      <AnalyticsBarChart
        title={t(`${T_PREFIX}.overview.chartTitle`)}
        subtitle={t(`${T_PREFIX}.overview.chartSubtitle`)}
        info={t(`${T_PREFIX}.overview.chartInfo`)}
        badge={badge}
        labels={rows.map((a) => name(a.addon))}
        datasets={stages.map((stage) => ({
          label: t(`${T_PREFIX}.stage.${stage.key}`),
          data: rows.map(stage.value),
          color: FUNNEL_COLORS[stage.key],
        }))}
        direction="horizontal"
        valueAxis="count"
        showValueLabels
        plotHeight={hbarPlotHeight(rows.length, 3)}
        chartOptions={barChartOptions}
      />
      <AnalyticsCard
        title={t(`${T_PREFIX}.overview.funnelTitle`)}
        subtitle={t(`${T_PREFIX}.overview.funnelSubtitle`)}
        badge={badge}
      >
        <AnalyticsTable<VisibleAddon>
          rowKey="addon"
          dataSource={rows}
          columns={columns}
          scroll={{ x: true }}
        />
      </AnalyticsCard>
    </Flex>
  );
};

export type ColumnTitleProps = { title: string; info: string };

// A column header with a "?" that explains only that column.
const ColumnTitle: React.FC<ColumnTitleProps> = ({ title, info }) => {
  return (
    <Flex align="center" justify="center" gap={4}>
      {title}
      <InfoTooltip content={info} />
    </Flex>
  );
};
